import { ArrowUpRight } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import useTabs from 'src/hooks/useTabs';

import { TAB_KEYS } from '../../constants';
import { athenaScheduleUrl, getCareOrchestrationPath } from '../../helpers';
import AthenaLink from '../AthenaLink';
import PlatformLink from '../PlatformLink';
import DismissVisitModal from './DismissVisitModal';
import StatusPills from './StatusPills';
import {
  activityEvents,
  anchorLabel,
  ATHENA_VISIT_TYPES,
  currentDeferral,
  formatShortDate,
  memberMeta,
  VISIT_NAMES,
  VISIT_RULES,
} from './visitsDue';
import styles from './VisitsDueDetail.module.scss';

const ACTIONABLE_STATES = ['overdue', 'due'];

const EVENT_LABELS = {
  created: 'Became due',
  dismissed: 'Dismissed',
  reopened: 'Reopened',
};

const DeferralFields = ({ reason, note, until, by, at }) => (
  <dl className={styles.fields}>
    <dt>Reason</dt>
    <dd>{reason}</dd>
    {until && (
      <>
        <dt>Until</dt>
        <dd>{formatShortDate(until)}</dd>
      </>
    )}
    <dt>Note</dt>
    <dd className={note ? undefined : styles.fieldEmpty}>{note ? `"${note}"` : 'No note'}</dd>
    {by && at && (
      <>
        <dt>By</dt>
        <dd>
          {by} · {formatShortDate(at)}
        </dd>
      </>
    )}
  </dl>
);

DeferralFields.propTypes = {
  reason: PropTypes.string,
  note: PropTypes.string,
  until: PropTypes.string,
  by: PropTypes.string,
  at: PropTypes.string,
};

const VisitsDueDetail = ({
  row,
  onDismiss,
  isDismissing,
  onDefer,
  isDeferring,
  onReopen,
  isReopening,
}) => {
  const history = row.history ?? [];
  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);
  const { onSelectTab } = useTabs();
  const integration = row.patient?.athenaIntegration;
  const athenaHref = athenaScheduleUrl(integration);
  const bookingType = ATHENA_VISIT_TYPES[VISIT_RULES[row.ruleId]?.visit];
  const isActionable = ACTIONABLE_STATES.includes(row.dueState);
  const isReopenable = ['deferred', 'dismissed'].includes(row.dueState);
  const deferral = isReopenable ? currentDeferral(row) : null;
  const activity = activityEvents(row);

  const openMemberTasks = () =>
    onSelectTab({
      path: getCareOrchestrationPath(TAB_KEYS.TASKS, {
        statusFilter: 'open',
        patientId: row.patientId,
      }),
    });

  return (
    <div className={styles.rail}>
      <div className={styles.section}>
        <div className={styles.header}>
          <div>
            <div className={styles.name}>{row.patient?.name ?? row.patientId}</div>
            <div className={styles.meta}>{memberMeta(row)}</div>
          </div>
          <div className={styles.pills}>
            <StatusPills row={row} />
          </div>
        </div>
        <PlatformLink patient={row.patient} patientId={row.patientId} />
        <AthenaLink integration={integration ?? null} />
      </div>

      {deferral && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {row.dueState === 'deferred' ? 'Deferred' : 'Dismissed'}
          </div>
          <DeferralFields
            reason={deferral.reason}
            note={deferral.note}
            until={row.dueState === 'deferred' ? deferral.heldUntil : null}
            by={deferral.by}
            at={deferral.at}
          />
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Why this is due</div>
        {row.taskDescription && <p className={styles.why}>{row.taskDescription}</p>}
        {anchorLabel(row) && <p className={styles.muted}>Anchor: {anchorLabel(row)}</p>}
        {row.bookedVisit && (
          <p className={styles.muted}>Booked for {formatShortDate(row.bookedVisit.date)}</p>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Visit history</div>
        {history.length === 0 && <p className={styles.muted}>No cognitive visits on record.</p>}
        {history.length > 0 && (
          <ul className={styles.history}>
            {history.map((visit) => {
              const isMatch = visit.booked && visit.code === VISIT_RULES[row.ruleId]?.visit;
              return (
                <li key={`${visit.code}-${visit.date}`} className={styles.historyItem}>
                  <span
                    className={`${styles.historyCode} ${isMatch ? styles.historyCodeMatch : ''}`}
                  >
                    {visit.code}
                  </span>
                  <span>{VISIT_NAMES[visit.code] ?? visit.code}</span>
                  <span className={styles.historyDate}>
                    {visit.booked ? 'booked ' : ''}
                    {formatShortDate(visit.date)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {isActionable && (bookingType || row.bookingGuardrails?.length > 0) && (
        <div className={styles.section}>
          {bookingType && (
            <p className={styles.why}>
              Book in Athena as <strong>{bookingType.typeName}</strong>, reason &quot;
              {bookingType.reason}&quot;.
            </p>
          )}
          {row.bookingGuardrails?.length > 0 && (
            <ul className={styles.guardrails}>
              {row.bookingGuardrails.map((guardrail) => (
                <li key={guardrail} className={styles.guardrail}>
                  {guardrail}
                </li>
              ))}
            </ul>
          )}
          <p className={styles.muted}>
            Book at least one week after any outstanding orders. The row turns Booked when the
            appointment comes back from Athena.
          </p>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Activity</div>
        {activity.length === 0 && <p className={styles.muted}>Nothing yet.</p>}
        <ul className={styles.activity}>
          {activity.map((event) => (
            <li key={`${event.kind}-${event.at}`} className={styles.activityItem}>
              <div className={styles.activityHead}>
                {event.kind === 'deferred'
                  ? `Deferred until ${formatShortDate(event.heldUntil)}`
                  : EVENT_LABELS[event.kind]}
              </div>
              {(event.kind === 'deferred' || event.kind === 'dismissed') && (
                <DeferralFields reason={event.reason} note={event.note} />
              )}
              <div className={styles.activityMeta}>
                {event.by ? `${event.by} · ` : ''}
                {formatShortDate(event.at)}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.footer}>
        {isActionable && (
          <>
            <a
              className={`${styles.button} ${styles.buttonPrimary}`}
              href={athenaHref ?? undefined}
              target={athenaHref ? '_blank' : undefined}
              rel={athenaHref ? 'noopener noreferrer' : undefined}
              aria-disabled={!athenaHref}
            >
              Book in Athena
              <ArrowUpRight size={14} aria-hidden="true" />
            </a>
            <button
              type="button"
              className={styles.button}
              disabled={isDismissing || isDeferring}
              onClick={() => setIsDismissModalOpen(true)}
            >
              Dismiss
            </button>
          </>
        )}
        {isReopenable && (
          <button
            type="button"
            className={styles.button}
            disabled={isReopening}
            onClick={() => onReopen(row)}
          >
            Reopen
          </button>
        )}
        <button
          type="button"
          className={`${styles.button} ${styles.buttonRight}`}
          onClick={openMemberTasks}
        >
          Open member
        </button>
      </div>

      {isDismissModalOpen && (
        <DismissVisitModal
          patientName={row.patient?.name}
          isSubmitting={isDismissing || isDeferring}
          onClose={() => setIsDismissModalOpen(false)}
          onDefer={(reevalOn, reason, note) => onDefer(row, reevalOn, reason, note)}
          onDismiss={(reason, note) => onDismiss(row, reason, note)}
        />
      )}
    </div>
  );
};

VisitsDueDetail.propTypes = {
  row: PropTypes.object.isRequired,
  onDismiss: PropTypes.func.isRequired,
  isDismissing: PropTypes.bool.isRequired,
  onDefer: PropTypes.func.isRequired,
  isDeferring: PropTypes.bool.isRequired,
  onReopen: PropTypes.func.isRequired,
  isReopening: PropTypes.bool.isRequired,
};

export default VisitsDueDetail;
